import {
  Component,
  inject,
  computed,
  HostListener,
  viewChild,
  ElementRef,
  signal,
  effect,
  input,
  output,
  NgZone,
  DestroyRef
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { CommonModule } from '@angular/common'
import { CdkDropListGroup } from '@angular/cdk/drag-drop'
import { KanbanService } from '../../kanban.service'
import { KanbanColumnComponent } from '../kanban-column/kanban-column.component'
import { CardDetailsDialogComponent } from '../card-details-dialog/card-details-dialog.component'
import { PhaseFormComponent } from '../../features/phase-form/phase-form.component'
import { Kanban } from '../../../../_services/kanban/kanban.model'
import { ActionsConfigProps, FieldConfig } from '../../entities/kanban-card/kanban-card.component'
import { LoadingComponent, PopupComponent, VisioPopupService } from '@visio-ai/ui-kit'
import { KanbanFiltersComponent } from '../kanban-filters/kanban-filters.component'
import { KanbanFilter } from '../kanban-filters/kanban-filters.model'
import { TagChangeEvent, TagsConfig } from '../../models/kanban.model'
import { environment } from '../../../../../environments/environment'

@Component({
  selector: 'app-kanban-board',
  standalone: true,
  providers: [KanbanService],
  imports: [
    CommonModule,
    KanbanColumnComponent,
    KanbanFiltersComponent,
    CdkDropListGroup,
    LoadingComponent,
    PopupComponent,
    CardDetailsDialogComponent,
    PhaseFormComponent
  ],
  templateUrl: './kanban-board.component.html',
  styleUrl: './kanban-board.component.scss'
})
export class KanbanBoardComponent {
  private kanbanService = inject(KanbanService)
  private columnsContainer = viewChild<ElementRef<HTMLElement>>('columnsContainer')
  private popupService = inject(VisioPopupService)
  private elementRef = inject(ElementRef)
  private ngZone = inject(NgZone)
  private destroyRef = inject(DestroyRef)
  protected isOverflowing = signal(false)

  actionsConfig = input<Record<string, ActionsConfigProps>>()
  apiUrl = input<string>()
  boardId = input<string>()
  fieldsToShow = input<FieldConfig[]>()
  boardFilters = input<KanbanFilter[]>([])
  tagsConfig = input<TagsConfig>({ allowEdit: false })
  showAdvancedFilters = input(true)
  userRoleMap = input<Record<string, 'reviewer' | 'cs'>>({})

  tagSelected = output<TagChangeEvent>()
  tagRemoved = output<TagChangeEvent>()

  protected selectedCard = signal<Kanban.Card.Entity | null>(null)
  protected showDetailsDialog = signal(false)
  protected loading = signal(false)
  protected columns = this.kanbanService.columns
  protected boardTitle = this.kanbanService.boardTitle
  protected selectedFilters = this.kanbanService.selectedFilters
  protected boardFields = this.kanbanService.fields
  protected tags = this.kanbanService.tags
  protected phases = this.kanbanService.phases

  protected phaseFormIsVisible = signal(false)
  protected phaseFormTask = signal<Kanban.Card.Entity | null>(null)
  protected phaseFormPhaseId = signal<string | null>(null)
  protected phaseFormPhaseName = signal('')
  protected connectedDropLists = computed(() => this.columns()?.map(column => column.phaseId))

  protected visibleColumns = computed(() => {
    const cols = this.columns()
    if (!cols) return cols
    const hiddenIds = new Set(
      this.selectedFilters()
        .filter(f => f.field.id === 'phaseId' && f.operator === 'neq')
        .map(f => f.value)
    )
    return hiddenIds.size === 0 ? cols : cols.filter(c => !hiddenIds.has(c.phaseId))
  })

  constructor() {
    this.exposePublicMethods()

    effect(() => {
      const apiUrl = this.apiUrl()
      if (apiUrl) {
        environment.apiURL = apiUrl
      }
    })
    effect(() => {
      if (this.columnsContainer()) {
        this.updateOverflowState()
      }
    })
    effect(() => {
      const boardId = this.boardId()
      if (boardId) {
        this.loading.set(true)
        this.kanbanService
          .getKanban(boardId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: response => {
              this.kanbanService.initBoard(response)
            },
            error: () => {
              this.popupService.callPopup({
                title: 'Erro ao carregar o board',
                body: 'Não foi possível carregar o board.',
                id: 'kanban-popup',
                type: 'error'
              })
              this.loading.set(false)
            },
            complete: () => this.loading.set(false)
          })
      }
    })
    let isFirstFiltersRun = true
    effect(() => {
      this.selectedFilters()
      if (isFirstFiltersRun) {
        isFirstFiltersRun = false
        return
      }
      this.initBoard()
    })
  }

  private exposePublicMethods(): void {
    const element = this.elementRef.nativeElement
    element.updateCard = (cardId: string) => this.ngZone.run(() => this.updateCard(cardId))
    element.initBoard = () => this.ngZone.run(() => this.initBoard())
  }

  @HostListener('window:resize')
  protected updateOverflowState() {
    const container = this.columnsContainer()?.nativeElement
    if (container) {
      const isOverflowing = container.offsetWidth < container.scrollWidth
      this.isOverflowing.set(isOverflowing)
    }
  }

  public initBoard() {
    const boardId = this.boardId()
    if (boardId) {
      this.kanbanService
        .getKanban(boardId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.kanbanService.initBoard(response)
          }
        })
    }
  }

  public updateCard(cardId: string): void {
    this.kanbanService.updateCard(cardId).subscribe()
  }

  protected onCardClick(card: Kanban.Card.Entity): void {
    this.selectedCard.set(card)
    setTimeout(() => this.showDetailsDialog.set(true))
  }

  protected onCloseDetailsDialog(): void {
    this.showDetailsDialog.set(false)
  }

  protected onTagSelected(tagChangeEvent: TagChangeEvent) {
    this.tagSelected.emit(tagChangeEvent)
  }

  protected onTagRemoved(tagChangeEvent: TagChangeEvent) {
    this.tagRemoved.emit(tagChangeEvent)
  }

  protected onOpenPhaseForm(card: Kanban.Card.Entity): void {
    this.phaseFormTask.set(card)
    this.phaseFormPhaseId.set(card.phaseId)
    this.phaseFormPhaseName.set(this.kanbanService.getPhaseName(card.phaseId))
    setTimeout(() => this.phaseFormIsVisible.set(true))
  }

  protected onPhaseFormSubmit(): void {
    const task = this.phaseFormTask()
    if (task) {
      this.updateCard(task.id)
    }
  }
}
